import { Button, Dropdown } from 'react-bootstrap';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useState, type MouseEvent, type ReactNode } from 'react';
import vconst from 'utils/helpers/structs/vconst';
import SvgUser from 'components/svg/SvgUser';
import SvgCheckSquare from 'components/svg/SvgCheckSquare';
import EToolTip from 'components/others/EToolTip';
import type { LeadProposalGenerationType, LeadType } from '@econtractor/types';
import type { NewObjectType } from './types';
import { useAuth } from 'context/AuthContext';
import { useSettings } from 'context/SettingsContext';
import useSettingsParams from 'hooks/useSettingsParams';
import { canViewSales, hasUserAccess, isManagerUser, isSellerUser } from 'utils/helpers/access/hasUserAccess';
import * as apiLead from 'api/apiLeads';
import RecordAccessOffcanvas from '../RecordAccessOffcanvas';
import { useNeedPay } from 'modules/admin';
import { buildNewObjectFromLead, buildNewSaleFromLead, buildNewServiceSaleFromLead } from './helpers/proposalAmountToObjectPrice';
import { getObjectCardPath } from 'utils/helpers/objects/objectCardPath';
import type { NewSaleType } from 'modules/objects';
import SolarIcon from 'components/icons/SolarIcon';

interface LeedCardToolBarProps {
    setNewEvent: (event: any) => void;
    lead?: LeadType | null;
    setNewObject: (obj: NewObjectType) => void;
    setNewSale?: (obj: NewSaleType) => void;
    setClientInfoId: (id: number | null) => void;
    onShowTasks: () => void;
    onRefresh: () => void | Promise<void>;
    acceptedGeneration?: LeadProposalGenerationType | null;
}

const LeedCardToolBar = ({
    setNewEvent,
    lead,
    setNewObject,
    setNewSale,
    setClientInfoId,
    onShowTasks,
    onRefresh,
    acceptedGeneration = null,
}: LeedCardToolBarProps) => {
    const { t } = useTranslation(['Leads', 'Clients', 'common']);
    const navigate = useNavigate();
    const { auth } = useAuth();
    const { localSettings } = useSettings();
    const { needPay } = useNeedPay();
    const params = useSettingsParams();
    const [showAccess, setShowAccess] = useState(false);
    const recordEdit = lead?.myAccess !== 'view';
    const canCreateObject = recordEdit && (hasUserAccess(auth?.user, 'objectCreate') || isSellerUser(auth?.user));
    const canCreateSale = recordEdit && canViewSales(auth?.user);
    const canShowTasks = params.tasksModule && params.tasksForLeads;
    const rate = Number(localSettings?.RATE?.current ?? 1) || 1;
    const isAccepted = lead?.leadStatus === vconst.leadAccept;
    const hasLinkedObject = Number(lead?.objectId || 0) > 0;
    const isLinkedService = Number(lead?.onlyService) === 1;
    const isLinkedSale = Number(lead?.onlySale) === 1 && !isLinkedService;
    const canCreateSaleItem = Boolean(canCreateSale && setNewSale && !hasLinkedObject);
    const canCreateObjectItem = canCreateObject && !hasLinkedObject;
    const showCreateMenu = canCreateObjectItem || canCreateSaleItem;

    const handleClickStatus = (e: MouseEvent<HTMLButtonElement>, statusID: number) => {
        e.currentTarget.blur();
        if (!lead) return;
        if (needPay()) return;
        setNewEvent({
            leadId: lead.ID,
            statusID,
            leadPendingDt:
                statusID === vconst.leadPostponed ? Number(lead.leadPendingDt) || 0 : 0,
        });
    };

    const handleCreateSelect = (create: () => void) => {
        if (!lead) return;
        if (needPay()) return;
        create();
    };

    const renderCreateItem = (icon: string, title: string, subtitle: string, onSelect: () => void) => (
        <Dropdown.Item
            as="button"
            onClick={(e) => {
                handleCreateSelect(onSelect);
                e.currentTarget.blur();
            }}
        >
            <div className="d-flex align-items-start w-100 text-start">
                <span className="d-inline-flex align-items-center justify-content-center flex-shrink-0 me-2 item-icon">
                    <SolarIcon name={icon} />
                </span>
                <div className="flex-grow-1 min-w-0">
                    <div className="fw-medium">{title}</div>
                    <small className="text-muted warehouse-team-menu-hint">{subtitle}</small>
                </div>
            </div>
        </Dropdown.Item>
    );

    const createMenu: ReactNode = (
        <Dropdown.Menu
            className="p-2 shadow-sm warehouse-team-menu"
            popperConfig={{ strategy: 'fixed' }}
            renderOnMount
        >
            {canCreateObjectItem
                ? renderCreateItem(
                    'brightness-high',
                    t('toolCreateObjectShort'),
                    t('toolCreateObjectHint'),
                    () => {
                        if (!lead) return;
                        setNewObject(buildNewObjectFromLead(lead, acceptedGeneration, rate));
                    },
                )
                : null}
            {canCreateSaleItem
                ? renderCreateItem(
                    'wallet2',
                    t('toolCreateSaleShort'),
                    t('toolCreateSaleHint'),
                    () => {
                        if (!lead || !setNewSale) return;
                        setNewSale(buildNewSaleFromLead(lead, acceptedGeneration, rate));
                    },
                )
                : null}
            {canCreateSaleItem
                ? renderCreateItem(
                    'briefcase',
                    t('toolCreateServiceSaleShort'),
                    t('toolCreateServiceSaleHint'),
                    () => {
                        if (!lead || !setNewSale) return;
                        setNewSale(buildNewServiceSaleFromLead(lead, acceptedGeneration, rate));
                    },
                )
                : null}
        </Dropdown.Menu>
    );

    return (
        <div className="lead-card-toolbar">
            {lead ? (
                <RecordAccessOffcanvas
                    show={showAccess}
                    setShow={setShowAccess}
                    grants={lead.accessUsers || []}
                    ownerId={lead.ownerId}
                    ownerName={lead.userName}
                    ownerEmail={lead.userEmail}
                    ownerPicture={lead.userPicture}
                    canManage={isManagerUser(auth?.user)}
                    onSave={async (grants) => {
                        const ok = await apiLead.setLeadAccess(lead.ID, grants);
                        if (ok) await onRefresh();
                        return ok;
                    }}
                />
            ) : null}
            <div className="lead-card-toolbar__actions">
                <Button
                    className="lead-card-toolbar__btn"
                    variant="success"
                    size="sm"
                    disabled={!lead || lead.myAccess === 'view'}
                    onClick={(e) => handleClickStatus(e, lead?.leadStatus || 0)}
                >
                    <SolarIcon name="plus" />
                    {t('leadEvent.btnAddEvent')}
                </Button>

                {showCreateMenu ? (
                    <Dropdown align="end">
                        <Dropdown.Toggle
                            className="lead-card-toolbar__btn toolbar-label-btn"
                            variant="success"
                            size="sm"
                            disabled={!lead || isAccepted}
                            id="lead-card-create-dropdown"
                        >
                            <SolarIcon name="plus" />
                            <span className="d-none d-md-inline ms-1">{t('toolCreate')}</span>
                        </Dropdown.Toggle>
                        {typeof document !== 'undefined' ? createPortal(createMenu, document.body) : createMenu}
                    </Dropdown>
                ) : null}

                {hasLinkedObject ? (
                <EToolTip title={isLinkedService ? t('toolGoToServiceSaleHint') : isLinkedSale ? t('toolGoToSaleHint') : t('toolGoToObjectHint')}>
                    <span className="d-inline-block">
                        <Button
                            className="lead-card-toolbar__btn toolbar-label-btn"
                            variant="success"
                            size="sm"
                            disabled={!lead}
                            onClick={(e) => {
                                e.currentTarget.blur();
                                if (!lead?.objectId) return;
                                navigate(getObjectCardPath(lead.objectId, isLinkedSale, isLinkedService));
                            }}
                        >
                            <SolarIcon name={isLinkedService ? 'briefcase' : isLinkedSale ? 'wallet2' : 'brightness-high'} />
                            <span className="d-none d-md-inline ms-1">
                                {isLinkedService ? t('toolGoToServiceSale') : isLinkedSale ? t('toolGoToSale') : t('toolGoToObject')}
                            </span>
                        </Button>
                    </span>
                </EToolTip>
                ) : null}

                {canShowTasks && (
                <EToolTip title={t('toolTask')}>
                    <span className="d-inline-block">
                        <Button
                            className="lead-card-toolbar__btn toolbar-label-btn"
                            size="sm"
                            disabled={!lead}
                            onClick={(e) => {
                                e.currentTarget.blur();
                                if (!lead) return;
                                onShowTasks();
                            }}
                        >
                            <SvgCheckSquare width={14} height={14} />
                            <span className="d-none d-md-inline ms-1">{t('toolTask')}</span>
                        </Button>
                    </span>
                </EToolTip>
                )}

                <EToolTip title={t('common:Users.accessGrant')}>
                    <span className="d-inline-block">
                        <Button
                            className="lead-card-toolbar__icon-btn toolbar-icon-btn"
                            size="sm"
                            disabled={!lead}
                            onClick={(e) => {
                                e.currentTarget.blur();
                                setShowAccess(true);
                            }}
                        >
                            <SolarIcon name="people" />
                        </Button>
                    </span>
                </EToolTip>

                <EToolTip title={t('common:toolRefreshList')}>
                    <span className="d-inline-block">
                        <Button
                            className="lead-card-toolbar__icon-btn toolbar-icon-btn"
                            size="sm"
                            disabled={!lead}
                            onClick={(e) => {
                                e.currentTarget.blur();
                                void onRefresh();
                            }}
                        >
                            <SolarIcon name="arrow-clockwise" />
                        </Button>
                    </span>
                </EToolTip>

                <EToolTip title={t('Clients:clientPopup.viewHint')}>
                    <span className="d-inline-block">
                        <Button
                            className="lead-card-toolbar__icon-btn toolbar-icon-btn"
                            size="sm"
                            disabled={!lead}
                            onClick={(e) => {
                                e.currentTarget.blur();
                                if (!lead) return;
                                setClientInfoId(lead?.clientId || null);
                            }}
                        >
                            <SvgUser width={14} height={14} />
                        </Button>
                    </span>
                </EToolTip>
            </div>
        </div>
    );
};

export default LeedCardToolBar;
